import abbreviateNumber from 'utils/shared/number/abbreviateNumber';

/**
 * `abbreviateNumber` returns the RAW number below 1,000 (`if (tier === 0) return number`),
 * so a sub-$1,000 premium carrying float error renders its full 16-digit expansion —
 * premium values off the feed genuinely look like `407420.00000000006`, and the
 * per-contract ratios below divide straight into that band. Rounding first is the guard.
 */
const abbreviateWhole = (value: number) => {
  const rounded = Math.round(Math.abs(value));
  if (rounded < 1000) return String(rounded);

  const abbreviated = String(abbreviateNumber(rounded, 1));
  // `abbreviateNumber` picks the tier before rounding the mantissa, so the top 0.05% of
  // each tier rounds up past it: $999,950,000 renders "1000.0M" instead of "1.0B".
  // Re-abbreviating one tier up is cheaper than changing the shared helper, which some
  // hundred other call sites depend on.
  return abbreviated.startsWith('1000.0')
    ? String(abbreviateNumber(rounded * 1.0005, 1))
    : abbreviated;
};

/** Abbreviated, unsigned dollar amount — `$4.8M`, `$407`. */
export const formatMoney = (value = 0) => `$${abbreviateWhole(value)}`;

/** Signed dollar amount — `+$40.4M`, `-$76.8M`, `$0`. */
export const formatSignedMoney = (value = 0) => {
  const prefix = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${prefix}${formatMoney(value)}`;
};

/** Abbreviated whole count — `263.1K`, `55`. */
export const formatCount = (value = 0) => abbreviateWhole(value);

/**
 * Average premium per contract — `$429`, `$1,234`. Em dash when there is nothing to
 * divide.
 *
 * Kept unabbreviated below $10,000: a $12.34 option is $1,234 a contract, which is
 * routine on SPX and NVDA, and `$1.2K` throws away the digits that make two sides
 * comparable. `$1,234` measures ~32px in a 90px column.
 */
export const formatPerContract = (premium = 0, contracts = 0) => {
  if (contracts <= 0) return '—';

  const perContract = Math.round(Math.abs(premium / contracts));
  return perContract < 10_000
    ? `$${perContract.toLocaleString('en-US')}`
    : formatMoney(perContract);
};

export default formatMoney;
